/**
 * writeAccess.tsx — which write controls the caller's membership role may see.
 *
 * The server enforces membership roles on every write route; this module only
 * HIDES the controls it would refuse, so a viewer is never offered a button
 * that ends in a 403. The role comes from GET /v1/me (session truth) — never
 * from the JWT claim. COSMETIC: the backend 403 stays the real boundary.
 *
 * FAIL CLOSED: no provider, no /me yet, a failed /me, an empty role (the
 * platform-operator case) or an unknown role all mean every flag is false and
 * every write control is hidden.
 *
 * Access levels (mirroring the server's route classes):
 *   records  — owner, admin: every sales / operations / HR / finance / payroll
 *              write, triage, approvals, agents + schedules, connects.
 *   platform — owner, admin, developer: run-step feedback and the other
 *              platform writes.
 *   owner    — owner only: tenant settings + logo, integration disconnect,
 *              email-routing departments, service accounts.
 *   member   — any recognised role (incl. billing, viewer): saved views.
 */

import { createContext, useContext } from 'react'

export type WriteAccess = {
  records: boolean
  platform: boolean
  owner: boolean
  /** Any active member with a recognised role — saved views only. */
  member: boolean
}

export const NO_WRITE_ACCESS: WriteAccess = Object.freeze({
  records: false,
  platform: false,
  owner: false,
  member: false,
})

/** The ONE role → access mapping. Pure; unknown/empty/missing role → nothing. */
export function writeAccessFor(role: string | null | undefined): WriteAccess {
  switch (role) {
    case 'owner':     return { records: true,  platform: true,  owner: true,  member: true }
    case 'admin':     return { records: true,  platform: true,  owner: false, member: true }
    case 'developer': return { records: false, platform: true,  owner: false, member: true }
    case 'billing':
    case 'viewer':    return { records: false, platform: false, owner: false, member: true }
    default:          return NO_WRITE_ACCESS
  }
}

/** The providers live in WriteAccessProvider.tsx (components only there). */
export const WriteAccessContext = createContext<WriteAccess>(NO_WRITE_ACCESS)

export function useWriteAccess(): WriteAccess {
  return useContext(WriteAccessContext)
}
